import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { ProjectGroupPermission, ProjectPermissionConstants } from "../../../constants";
import {
  GetGroupDetailsResponse as GetPublicGroupDetailsResponse,
  getPublicGroupDetails,
} from "../../../service/publicquery";
import {
  getCurrentUserProjectPermissions,
  GetCurrentUserProjectPermissionsResponse,
  getGroupDetails,
  GetGroupDetailsResponse,
} from "../../../service/query";
import { getCurrentUserGroupPermission, searchAclEntries } from "../../../service/query/QueryService.full";
import { BaseResponseWithMultiple, Status } from "../../../service/Shared";
import { useAuth } from "../../../useAuth";

type LandholdingPageRouteParams = "landholdingUuid";

interface LandholdingProviderProps {
  children?: JSX.Element;
}

interface LandholdingContextType {
  landholdingUuid: string | undefined;
  landholdingDetails: GetGroupDetailsResponse | undefined;
  publicLandholdingDetails: GetPublicGroupDetailsResponse | undefined;
  currentUserProjectPermissionsResponse: GetCurrentUserProjectPermissionsResponse[] | undefined;
  readGroupProjectsPermission: ProjectGroupPermission | undefined;
  hasGroupPermission: (permission: string) => boolean;
  getGroupPermission: (permission: string) => Promise<ProjectGroupPermission>;
}

export const LandholdingContext = createContext<LandholdingContextType>({} as LandholdingContextType);

export const LandholdingProvider = ({ children }: LandholdingProviderProps): JSX.Element => {
  const { landholdingUuid } = useParams<LandholdingPageRouteParams>();
  const { currentOrganisationUuid } = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  const [landholdingDetails, setLandholdingDetails] = useState<GetGroupDetailsResponse | undefined>(undefined);
  const [publicLandholdingDetails, setPublicLandholdingDetails] = useState<GetPublicGroupDetailsResponse | undefined>(
    undefined
  );

  const [currentUserProjectPermissionsResponse, setCurrentUserProjectPermissionsResponse] = useState<
    GetCurrentUserProjectPermissionsResponse[] | undefined
  >();
  const [readGroupProjectsPermission, setReadGroupProjectsPermission] = useState<ProjectGroupPermission>();
  const [groupPermissions, setGroupPermissions] = useState<string[]>([]);

  const getGroupPermission = async (permission: string): Promise<ProjectGroupPermission> => {
    if (landholdingUuid) {
      const res = await getCurrentUserGroupPermission({
        groupUuid: landholdingUuid,
        permission,
      });
      return res.data ? (res.data.accessLevel as ProjectGroupPermission) : ProjectGroupPermission.NONE;
    }

    return ProjectGroupPermission.NONE;
  };

  const fetchData = useCallback(async () => {
    if (landholdingUuid) {
      let currentUserProjectPermissionsRes:
        | BaseResponseWithMultiple<GetCurrentUserProjectPermissionsResponse>
        | undefined;

      const aclPromise = currentOrganisationUuid
        ? searchAclEntries({ resourceUuid: landholdingUuid, subjectUuid: currentOrganisationUuid })
        : Promise.resolve(undefined);

      const [groupPermission, aclRes] = await Promise.all([
        getGroupPermission(ProjectPermissionConstants.READ_PROJECT),
        aclPromise,
      ]);
      setReadGroupProjectsPermission(groupPermission);

      if (aclRes?.status === Status.Success && aclRes.data) {
        setGroupPermissions(aclRes.data[0]?.permissions ?? []);
      }

      const publicGroupDetailsRes = await getPublicGroupDetails({ projectGroupUuid: landholdingUuid });
      if (publicGroupDetailsRes.status === Status.Success && publicGroupDetailsRes.data) {
        if (groupPermission === ProjectGroupPermission.NONE) setPublicLandholdingDetails(publicGroupDetailsRes.data);
        currentUserProjectPermissionsRes = await getCurrentUserProjectPermissions({
          groupUuids: [],
          projectUuids: (publicGroupDetailsRes.data as GetPublicGroupDetailsResponse).projects.map((p) => p.uuid),
        });
      }
      if (currentUserProjectPermissionsRes?.status === Status.Success && currentUserProjectPermissionsRes.data) {
        setCurrentUserProjectPermissionsResponse(currentUserProjectPermissionsRes.data);
      }

      // If has partial / full access also call private api as it contains more fields
      if (groupPermission !== ProjectGroupPermission.NONE) {
        const groupDetailsRes = await getGroupDetails({
          projectGroupUuid: landholdingUuid,
        });

        if (groupDetailsRes.status === Status.Success && groupDetailsRes.data) {
          setLandholdingDetails(groupDetailsRes.data);
        }
      }
    }
  }, [landholdingUuid, currentOrganisationUuid]);

  useEffect(() => {
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData, landholdingUuid]);

  const hasGroupPermission = (permission: string): boolean => groupPermissions.includes(permission);

  const memoisedValue = useMemo(
    () => ({
      landholdingUuid,
      landholdingDetails,
      publicLandholdingDetails,
      currentUserProjectPermissionsResponse,
      readGroupProjectsPermission,
      hasGroupPermission,
      getGroupPermission,
    }),
    [
      landholdingUuid,
      landholdingDetails,
      publicLandholdingDetails,
      currentUserProjectPermissionsResponse,
      readGroupProjectsPermission,
      groupPermissions,
      getGroupPermission,
      fetchData,
    ]
  );

  return <LandholdingContext.Provider value={memoisedValue}>{!isLoading && children}</LandholdingContext.Provider>;
};
